import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Save } from "lucide-react";
import { toast } from "../../components/ui/sonner";

const toggleIn = (list, v, on) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));

const CheckList = ({ items, selected, onToggle, testid, render }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-80 overflow-y-auto border border-slate-200 rounded p-3 bg-white">
    {items.map((it) => (
      <label key={it.key} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
        <input type="checkbox" className="h-4 w-4 accent-slate-900" checked={selected.includes(it.key)}
          onChange={(e) => onToggle(it.key, e.target.checked)} data-testid={`${testid}-${it.key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
        {render(it)}
      </label>
    ))}
  </div>
);

export function QuoteApprovalsTab() {
  const [cfg, setCfg] = useState(null);
  const [labourTypes, setLabourTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/quote-approvals/settings").then((r) => setCfg({ labour_types: r.data.labour_types, approver_ids: r.data.approver_ids }));
    api.get("/branches").then((r) => setLabourTypes((r.data || []).map((b) => ({ key: b.name }))));
    api.get("/users?status=approved").then((r) => setUsers((r.data || []).map((u) => ({ key: u.id, ...u }))));
  }, []);

  if (!cfg) return <div className="text-sm text-slate-500">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/quote-approvals/settings", cfg);
      toast.success("Approval settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="quote-approvals-tab">
      <p className="text-sm text-slate-600">
        Quotes saved with one of these labour types can be saved as normal, but can't be printed or exported until
        an approver switches on the green Approve slider. Editing and saving an approved quote resets its approval.
      </p>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">Labour types that need approval</div>
        <CheckList items={[...labourTypes, ...cfg.labour_types.filter((t) => !labourTypes.some((l) => l.key === t)).map((t) => ({ key: t }))]}
          selected={cfg.labour_types} testid="approval-labour-type" render={(it) => it.key}
          onToggle={(k, on) => setCfg({ ...cfg, labour_types: toggleIn(cfg.labour_types, k, on) })} />
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">Who can approve</div>
        <p className="text-xs text-slate-500">Admins can always approve.</p>
        <CheckList items={users.filter((u) => u.role !== "admin")} selected={cfg.approver_ids} testid="approval-user"
          render={(u) => <span>{u.name || u.email} <span className="text-xs text-slate-400">{u.email}</span></span>}
          onToggle={(k, on) => setCfg({ ...cfg, approver_ids: toggleIn(cfg.approver_ids, k, on) })} />
      </div>
      <button onClick={save} disabled={saving} data-testid="approval-settings-save"
        className="h-10 px-5 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-60">
        <Save size={14} /> {saving ? "Saving…" : "Save approval settings"}
      </button>
    </div>
  );
}
